import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FileText, CheckCircle2, RefreshCw, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const LeadMagnet = ({ addToast }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/guide-request`, { name: form.name, email: form.email, source: "homepage-lead-magnet" });
      setDone(true);
      addToast && addToast("Guide sent! Check your inbox.", "success");
    } catch (err) {
      addToast && addToast("Something went wrong. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gray-50 border-y border-gray-100" data-testid="lead-magnet-section">
      <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
        {/* Left: pitch */}
        <div className="space-y-5">
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 border border-amber-200 rounded text-amber-800 text-xs font-bold uppercase tracking-widest font-heading">
            <FileText size={14} /> Free Download
          </span>
          <h2 className="text-3xl sm:text-4xl font-heading text-blue-900 leading-tight">The Florida Homeowner's Septic Replacement Guide</h2>
          <p className="text-gray-600 font-body leading-relaxed">
            Everything you need to know before replacing a septic system in Miami-Dade or Broward — written by licensed pros. Get it free, instantly.
          </p>
          <ul className="space-y-2 text-sm text-gray-600">
            {["7 warning signs your septic system is failing", "What replacement really costs in South Florida", "How permitting & soil testing actually works", "5 questions to ask before hiring any contractor"].map((t) => (
              <li key={t} className="flex items-start gap-2"><CheckCircle2 size={18} className="text-emerald-500 mt-0.5" /><span>{t}</span></li>
            ))}
          </ul>
        </div>

        {/* Right: form / success */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-7">
          {!done ? (
            <form onSubmit={submit} className="space-y-4" data-testid="lead-magnet-form">
              <h3 className="text-xl font-heading text-blue-950">Get your free guide</h3>
              <input data-testid="lead-magnet-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="First Name *" className="w-full h-12 px-4 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <input data-testid="lead-magnet-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email Address *" className="w-full h-12 px-4 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <button data-testid="lead-magnet-submit" type="submit" disabled={submitting} className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl shadow transition disabled:bg-gray-200 flex items-center justify-center gap-2">
                {submitting ? <RefreshCw className="animate-spin" size={18} /> : <><Download size={18} /> Send Me The Guide</>}
              </button>
              <p className="text-[11px] text-gray-400 text-center">No spam. Unsubscribe anytime. We respect your privacy.</p>
            </form>
          ) : (
            <div className="text-center space-y-4 py-4" data-testid="lead-magnet-success">
              <CheckCircle2 size={44} className="text-emerald-500 mx-auto" />
              <h3 className="text-xl font-heading text-blue-950">Check your inbox!</h3>
              <p className="text-sm text-gray-600">We've emailed your guide. You can also read it right now:</p>
              <Link to="/resources/septic-replacement-guide" data-testid="lead-magnet-read-now" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-900 hover:bg-blue-800 text-white font-bold transition">
                <FileText size={18} /> Read the Guide Now
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default LeadMagnet;
